import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { PatientFormData } from '../types/patient'
import { PATIENT_SALUTATIONS } from '../types/salutation'
import { getAddressLabels, getDefaultCountry, formatEircode, validateEircode, formatPhoneNumber } from '../utils/locale'
import { formatUserName } from '../utils/name'
import { userService } from '../services/userService'
import { gmsService } from '../services/gmsService'
import type { GMSEligibilityResult } from '../types/gms'
import { Accordion, AccordionSectionConfig } from './Accordion'
import './PatientForm.css'

// Create schema with i18n support
const createPatientSchema = (t: (key: string) => string) => z.object({
  salutation: z
    .enum(['Mr', 'Mrs', 'Miss', 'Ms', 'Mx', 'Dr', 'Prof', 'Rev'])
    .optional()
    .or(z.literal('')),
  firstName: z
    .string()
    .min(1, t('form.required'))
    .min(2, t('form.minLength').replace('{{min}}', '2')),
  middleName: z.string().optional(),
  lastName: z
    .string()
    .min(1, t('form.required'))
    .min(2, t('form.minLength').replace('{{min}}', '2')),
  dateOfBirth: z.string().min(1, t('form.required')),
  gender: z
    .enum(['male', 'female', 'other', 'unknown'])
    .or(z.literal(''))
    .refine((val) => val !== '', { message: t('form.required') }),
  maritalStatus: z.enum(['single', 'married', 'divorced', 'widowed', 'separated', 'unknown']).optional().or(z.literal('')),
  email: z.string().email(t('form.invalidEmail')).optional().or(z.literal('')),
  // Phone is recommended but no longer strictly required for registration
  phone: z
    .string()
    .regex(/^[\d\s\-\+\(\)]+$/, t('form.invalidPhone'))
    .optional()
    .or(z.literal('')),
  alternatePhone: z.string().optional(),
  // Address – require at least two lines: here we enforce street and city
  street: z
    .string()
    .min(1, t('form.required'))
    .min(2, t('form.minLength').replace('{{min}}', '2')),
  city: z
    .string()
    .min(1, t('form.required'))
    .min(2, t('form.minLength').replace('{{min}}', '2')),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
  emergencyContactName: z.string().optional(),
  emergencyContactRelationship: z.string().optional(),
  emergencyContactPhone: z.string().optional(),
  emergencyContactEmail: z.string().email(t('form.invalidEmail')).optional().or(z.literal('')),
  bloodType: z.enum(['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'unknown']).optional().or(z.literal('')),
  allergies: z.string().optional(),
  medicalHistory: z.string().optional(),
  currentMedications: z.string().optional(),
  pastMedications: z.string().optional(),
  chronicConditions: z.string().optional(),
  // Health identifiers
  ppsNumber: z.string().optional(),
  gmsNumber: z.string().optional(),
  ihiNumber: z.string().optional(),
  // Lifestyle factors
  smokingStatus: z.enum(['never', 'ex-smoker', 'current-daily', 'current-occasional', 'e-cigarette']).optional().or(z.literal('')),
  smokingDescription: z.string().optional(),
  smokingCessationDetails: z.string().optional(),
  smokingAmountPerDay: z.number().optional(),
  smokingYearsSmoking: z.number().optional(),
  alcoholStatus: z.enum(['non-drinker', 'ex-drinker', 'current-drinker']).optional().or(z.literal('')),
  alcoholDescription: z.string().optional(),
  alcoholFrequency: z.string().optional(),
  alcoholQuantityPerOccasion: z.string().optional(),
  alcoholType: z.string().optional(),
  alcoholBingeDrinking: z.boolean().optional(),
  alcoholBingeFrequency: z.string().optional(),
  // Coverage & schemes (simple validation; details validated in backend/domain)
  patientType: z.enum(['private', 'gms', 'doctorVisitCard', 'other']).optional().or(z.literal('')),
  insuranceProvider: z.string().optional(),
  insurancePolicyNumber: z.string().optional(),
  insuranceGroupNumber: z.string().optional(),
  insuranceCoverType: z.string().optional(),
  gmsReviewDate: z.string().optional(),
  gmsDoctorNumber: z.string().optional(),
  gmsDistanceCode: z.string().optional(),
  gmsDispensingStatus: z.string().optional(),
  gmsEligibilityStartDate: z.string().optional(),
  gmsEligibilityEndDate: z.string().optional(),
  doctorVisitCardNumber: z.string().optional(),
  doctorVisitCardReviewDate: z.string().optional(),
  doctorVisitCardDoctorNumber: z.string().optional(),
  doctorVisitCardDistanceCode: z.string().optional(),
  dpsNumber: z.string().optional(),
  dpsExpiryDate: z.string().optional(),
  companySchemeAnPost: z.boolean().optional(),
  companySchemeGarda: z.boolean().optional(),
  companySchemeOther: z.string().optional(),
  // Extended identifiers
  hospitalIdentifierSystem: z.string().optional(),
  hospitalIdentifierValue: z.string().optional(),
  labIdentifierSystem: z.string().optional(),
  labIdentifierValue: z.string().optional(),
  // Socio-cultural & communication
  occupation: z.string().optional(),
  religion: z.string().optional(),
  ethnicity: z
    .enum([
      'whiteIrish',
      'irishTraveller',
      'otherWhite',
      'blackIrishOrAfrican',
      'otherBlack',
      'chinese',
      'otherAsian',
      'other',
      'notStated',
    ])
    .optional()
    .or(z.literal('')),
  preferredLanguage: z.string().optional(),
  interpreterRequired: z.boolean().optional(),
  transportNeeds: z.string().optional(),
  advocacyNeeds: z.string().optional(),
  // Practice registration
  registeredGpName: z
    .string()
    .min(1, t('form.required'))
    .min(2, t('form.minLength').replace('{{min}}', '2')),
  registeredGpId: z.string().optional(),
  usualGpName: z.string().optional(),
  branchName: z.string().optional(),
  registrationDate: z.string().optional(),
  deregistrationDate: z.string().optional(),
  deregistrationReason: z.string().optional(),
  primaryCareTeamEnrolmentStatus: z.string().optional(),
  primaryCareTeamEnrolmentDate: z.string().optional(),
  primaryCareTeamDeEnrolmentDate: z.string().optional(),
  // End of life
  dateOfDeath: z.string().optional(),
  causesOfDeath: z.string().optional(),
  // Next of kin
  nokTitle: z.enum(['Mr', 'Mrs', 'Miss', 'Ms', 'Mx', 'Dr', 'Prof', 'Rev']).optional().or(z.literal('')),
  nokFirstName: z.string().optional(),
  nokFamilyName: z.string().optional(),
  nokGender: z.enum(['male', 'female', 'other', 'unknown']).optional().or(z.literal('')),
  nokRelationshipToPatient: z.string().optional(),
  nokHomePhone: z.string().optional(),
  nokMobilePhone: z.string().optional(),
  nokEmail: z.string().email(t('form.invalidEmail')).optional().or(z.literal('')),
  nokAddressLine1: z.string().optional(),
  nokAddressLine2: z.string().optional(),
  nokCityTown: z.string().optional(),
  nokCounty: z.string().optional(),
  nokEircode: z.string().optional(),
  nokCountry: z.string().optional(),
  // Carer
  carerTitle: z.enum(['Mr', 'Mrs', 'Miss', 'Ms', 'Mx', 'Dr', 'Prof', 'Rev']).optional().or(z.literal('')),
  carerFirstName: z.string().optional(),
  carerFamilyName: z.string().optional(),
  carerGender: z.enum(['male', 'female', 'other', 'unknown']).optional().or(z.literal('')),
  carerDateOfBirth: z.string().optional(),
  carerRelationshipToPatient: z.string().optional(),
  carerIsRegisteredPatient: z.boolean().optional(),
  carerLinkedPatientId: z.string().optional(),
  carerHomePhone: z.string().optional(),
  carerMobilePhone: z.string().optional(),
  carerEmail: z.string().email(t('form.invalidEmail')).optional().or(z.literal('')),
  carerAddressLine1: z.string().optional(),
  carerAddressLine2: z.string().optional(),
  carerCityTown: z.string().optional(),
  carerCounty: z.string().optional(),
  carerEircode: z.string().optional(),
  carerCountry: z.string().optional(),
  carerConsentRecordedAt: z.string().optional(),
  // Family and Relationships
  familyId: z.string().optional(),
  guardianId: z.string().optional(),
  isDependent: z.boolean().optional(),
  notes: z.string().optional(),
})

interface PatientFormProps {
  initialData?: Partial<PatientFormData>
  /** When editing, pass patient id so "Check eligibility" can store result for this patient */
  patientId?: string
  onSubmit: (data: PatientFormData) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
}

export function PatientForm({ initialData, patientId, onSubmit, onCancel, isLoading }: PatientFormProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const addressLabels = getAddressLabels()
  const defaultCountry = getDefaultCountry()
  const { data: clinicalStaff } = useQuery({
    queryKey: ['clinical-staff'],
    queryFn: () => userService.getClinicalStaff(),
  })
  
  // Extract hash from URL for section navigation
  const hashSection = location.hash || undefined

  const schema = createPatientSchema(t)
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    setError,
  } = useForm<PatientFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      salutation: initialData?.salutation,
      ...initialData,
      country: initialData?.country || defaultCountry,
    },
  })

  const registeredGpId = watch('registeredGpId')
  const registeredGpName = watch('registeredGpName')

  useEffect(() => {
    if (!clinicalStaff?.length || registeredGpId) return
    if (!registeredGpName?.trim()) return
    const match = clinicalStaff.find((s) => formatUserName(s) === registeredGpName.trim())
    if (match) setValue('registeredGpId', match.id)
  }, [clinicalStaff, registeredGpName, registeredGpId, setValue])

  const postalCode = watch('postalCode')
  const gmsNumber = watch('gmsNumber')
  const [eligibilityResult, setEligibilityResult] = useState<GMSEligibilityResult | null>(null)
  const [eligibilityError, setEligibilityError] = useState<string | null>(null)

  const eligibilityMutation = useMutation({
    mutationFn: (schemeId: string) => gmsService.checkEligibility(schemeId, patientId),
    onSuccess: (data) => {
      setEligibilityResult(data)
      setEligibilityError(null)
    },
    onError: (err: unknown) => {
      setEligibilityError(err instanceof Error ? err.message : 'Eligibility check failed')
      setEligibilityResult(null)
    },
  })

  const handleCheckEligibility = () => {
    const schemeId = (gmsNumber || '').trim()
    if (!schemeId) return
    setEligibilityResult(null)
    setEligibilityError(null)
    eligibilityMutation.mutate(schemeId)
  }

  const handleApplyEligibilityToForm = () => {
    if (!eligibilityResult || !eligibilityResult.found) return
    setValue('gmsNumber', eligibilityResult.schemeId || gmsNumber || '')
    if (eligibilityResult.doctorNumber) setValue('gmsDoctorNumber', eligibilityResult.doctorNumber)
    setValue('patientType', eligibilityResult.schemeType === 'DVC' ? 'doctorVisitCard' : 'gms')
    if (eligibilityResult.eligibilityStartDate) setValue('gmsEligibilityStartDate', eligibilityResult.eligibilityStartDate)
    if (eligibilityResult.eligibilityEndDate) setValue('gmsEligibilityEndDate', eligibilityResult.eligibilityEndDate)
    if (eligibilityResult.schemeType === 'DVC') {
      setValue('doctorVisitCardNumber', eligibilityResult.schemeId || '')
      if (eligibilityResult.doctorNumber) setValue('doctorVisitCardDoctorNumber', eligibilityResult.doctorNumber)
    }
    setEligibilityResult(null)
  }

  // Format Eircode on blur
  const handlePostalCodeBlur = () => {
    if (postalCode) {
      const formatted = formatEircode(postalCode)
      setValue('postalCode', formatted)
    }
  }

  // Format phone number on blur
  const handlePhoneBlur = (field: 'phone' | 'alternatePhone' | 'emergencyContactPhone') => {
    const value = watch(field)
    if (value) {
      const formatted = formatPhoneNumber(value)
      setValue(field, formatted)
    }
  }

  const handleFormSubmit = async (data: PatientFormData) => {
    const countryValue = (data.country || defaultCountry || '').trim().toLowerCase()
    const shouldValidateEircode =
      addressLabels.postalCode === 'Eircode' && countryValue.includes('ireland')

    if (shouldValidateEircode && data.postalCode && !validateEircode(data.postalCode)) {
      setError('postalCode', {
        type: 'manual',
        message: t('form.invalidEircode') || 'Invalid Eircode',
      })
      return
    }
    await onSubmit(data)
  }

  const sections: AccordionSectionConfig[] = [
    {
      id: 'identity',
      title: t('common.demographics'),
      description: t('patients.identityAndNames') || 'Identity & Names',
      initiallyOpen: true,
    },
    {
      id: 'contact-address',
      title: t('common.contact') || 'Contact & Address',
      description: t('patients.contactAndAddressDescription') || 'Phones, email and address for correspondence',
      initiallyOpen: true,
    },
    {
      id: 'coverage',
      title: t('patients.coverageAndSchemes') || 'Health Coverage & Schemes',
      description: t('patients.coverageAndSchemesDescription') || 'GMS, Doctor Visit Card, DPS, private insurance, company schemes',
    },
    {
      id: 'identifiers',
      title: t('patients.identifiers') || 'Identifiers',
      description: t('patients.identifiersDescription') || 'PPS, IHI, internal and external identifiers',
    },
    {
      id: 'socio-cultural',
      title: t('patients.socioCulturalAndCommunication') || 'Socio-cultural & Communication',
      description: t('patients.socioCulturalAndCommunicationDescription') || 'Occupation, religion, ethnicity, language and support needs',
    },
    {
      id: 'practice-registration',
      title: t('patients.practiceRegistration') || 'Practice Registration',
      description: t('patients.practiceRegistrationDescription') || 'Registered GP, usual GP, branch and enrolment',
    },
    {
      id: 'end-of-life',
      title: t('patients.endOfLife') || 'End of Life',
      description: t('patients.endOfLifeDescription') || 'Date and cause(s) of death',
    },
    {
      id: 'next-of-kin',
      title: t('patients.nextOfKin') || 'Next of Kin',
      description: t('patients.nextOfKinDescription') || 'Primary next of kin details',
    },
    {
      id: 'carers',
      title: t('patients.carers') || 'Carers',
      description: t('patients.carersDescription') || 'Carers supporting the patient',
    },
    {
      id: 'clinical-and-lifestyle',
      title: t('patients.clinicalAndLifestyle') || 'Clinical & Lifestyle',
      description: t('patients.clinicalAndLifestyleDescription') || 'Medical information, lifestyle factors and notes',
    },
  ]

  const renderSection = (sectionId: string) => {
    switch (sectionId) {
      case 'identity':
        return (
          <div className="form-section" role="group" aria-labelledby="demographics-heading">
            <h2 id="demographics-heading">{t('common.demographics')}</h2>
            
            {/* Name fields row */}
            <div className="form-row form-row-4">
              <div className="form-group">
                <label htmlFor="salutation">
                  {t('patients.salutation')}{' '}
                  <span className="optional" aria-label={t('accessibility.optional')}>
                    ({t('common.optional')})
                  </span>
                </label>
                <select
                  id="salutation"
                  {...register('salutation')}
                  className={errors.salutation ? 'error' : ''}
                  aria-invalid={!!errors.salutation}
                >
                  <option value="">{t('common.none')}</option>
                  {PATIENT_SALUTATIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="firstName">
                  {t('patients.firstName')}{' '}
                  <span className="required" aria-label={t('accessibility.required')}>
                    *
                  </span>
                </label>
                <input
                  id="firstName"
                  type="text"
                  {...register('firstName')}
                  className={errors.firstName ? 'error' : ''}
                  aria-required="true"
                  aria-invalid={!!errors.firstName}
                  aria-describedby={errors.firstName ? 'firstName-error' : undefined}
                  autoComplete="given-name"
                />
                {errors.firstName && (
                  <span id="firstName-error" className="error-message" role="alert">
                    {errors.firstName.message}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="middleName">
                  {t('patients.middleName')}{' '}
                  <span className="optional" aria-label={t('accessibility.optional')}>
                    ({t('common.optional')})
                  </span>
                </label>
                <input
                  id="middleName"
                  type="text"
                  {...register('middleName')}
                  autoComplete="additional-name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="lastName">
                  {t('patients.lastName')}{' '}
                  <span className="required" aria-label={t('accessibility.required')}>
                    *
                  </span>
                </label>
                <input
                  id="lastName"
                  type="text"
                  {...register('lastName')}
                  className={errors.lastName ? 'error' : ''}
                  aria-required="true"
                  aria-invalid={!!errors.lastName}
                  aria-describedby={errors.lastName ? 'lastName-error' : undefined}
                  autoComplete="family-name"
                />
                {errors.lastName && (
                  <span id="lastName-error" className="error-message" role="alert">
                    {errors.lastName.message}
                  </span>
                )}
              </div>
            </div>

            {/* Demographics row */}
            <div className="form-row form-row-3">
              <div className="form-group">
                <label htmlFor="dateOfBirth">
                  {t('patients.dateOfBirth')}{' '}
                  <span className="required" aria-label={t('accessibility.required')}>
                    *
                  </span>
                </label>
                <input
                  id="dateOfBirth"
                  type="date"
                  {...register('dateOfBirth')}
                  className={errors.dateOfBirth ? 'error' : ''}
                  aria-required="true"
                  aria-invalid={!!errors.dateOfBirth}
                  aria-describedby={errors.dateOfBirth ? 'dateOfBirth-error' : undefined}
                  max={new Date().toISOString().split('T')[0]}
                />
                {errors.dateOfBirth && (
                  <span id="dateOfBirth-error" className="error-message" role="alert">
                    {errors.dateOfBirth.message}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="gender">
                  {t('patients.gender')}{' '}
                  <span className="required" aria-label={t('accessibility.required')}>
                    *
                  </span>
                </label>
                <select
                  id="gender"
                  {...register('gender')}
                  className={errors.gender ? 'error' : ''}
                  aria-required="true"
                  aria-invalid={!!errors.gender}
                  aria-describedby={errors.gender ? 'gender-error' : undefined}
                >
                  <option value="">{t('common.select') || 'Select...'}</option>
                  <option value="unknown">{t('gender.unknown')}</option>
                  <option value="male">{t('gender.male')}</option>
                  <option value="female">{t('gender.female')}</option>
                  <option value="other">{t('gender.other')}</option>
                </select>
                {errors.gender && (
                  <span id="gender-error" className="error-message" role="alert">
                    {errors.gender.message}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="maritalStatus">
                  {t('patients.maritalStatus')}{' '}
                  <span className="optional" aria-label={t('accessibility.optional')}>
                    ({t('common.optional')})
                  </span>
                </label>
                <select id="maritalStatus" {...register('maritalStatus')}>
                  <option value="">{t('common.select') || 'Select...'}</option>
                  <option value="single">{t('maritalStatus.single')}</option>
                  <option value="married">{t('maritalStatus.married')}</option>
                  <option value="divorced">{t('maritalStatus.divorced')}</option>
                  <option value="widowed">{t('maritalStatus.widowed')}</option>
                  <option value="separated">{t('maritalStatus.separated')}</option>
                </select>
              </div>
            </div>

            {/* Identifiers row */}
            <div className="form-row form-row-2">
              <div className="form-group">
                <label htmlFor="ppsNumber">
                  PPS Number{' '}
                  <span className="optional" aria-label={t('accessibility.optional')}>
                    ({t('common.optional')})
                  </span>
                </label>
                <input
                  id="ppsNumber"
                  type="text"
                  {...register('ppsNumber')}
                  autoComplete="off"
                  placeholder="e.g., 1234567A"
                />
              </div>
              <div className="form-group">
                <label htmlFor="gmsNumber">
                  GMS Number{' '}
                  <span className="optional" aria-label={t('accessibility.optional')}>
                    ({t('common.optional')})
                  </span>
                </label>
                <div className="form-row form-row-2" style={{ alignItems: 'flex-end', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <input
                    id="gmsNumber"
                    type="text"
                    {...register('gmsNumber')}
                    autoComplete="off"
                    placeholder="e.g. 1234567A or A045523B"
                    aria-describedby={eligibilityError ? 'gms-eligibility-error' : eligibilityResult ? 'gms-eligibility-result' : undefined}
                  />
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={handleCheckEligibility}
                    disabled={!(gmsNumber || '').trim() || eligibilityMutation.isPending}
                    aria-label="Check eligibility with HSE"
                  >
                    {eligibilityMutation.isPending ? 'Checking…' : 'Check eligibility'}
                  </button>
                </div>
                {eligibilityError && (
                  <span id="gms-eligibility-error" className="error-message" role="alert">
                    {eligibilityError}
                  </span>
                )}
                {eligibilityResult && !eligibilityResult.found && (
                  <p id="gms-eligibility-result" className="form-hint" role="status">
                    Patient not found on GMS or DVC schemes.
                  </p>
                )}
                {eligibilityResult && eligibilityResult.found && (
                  <div id="gms-eligibility-result" className="form-hint" role="status">
                    <p>
                      {eligibilityResult.schemeType} – {eligibilityResult.eligibilityStatus ?? 'Valid'}
                      {eligibilityResult.doctorNumber && ` · Doctor: ${eligibilityResult.doctorNumber}`}
                      {eligibilityResult.eligibilityEndDate && ` · End: ${eligibilityResult.eligibilityEndDate}`}
                    </p>
                    <button
                      type="button"
                      className="button button-secondary"
                      onClick={handleApplyEligibilityToForm}
                    >
                      Apply to form
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      case 'contact-address':
        return (
          <>
            <div className="form-section" role="group" aria-labelledby="contact-heading">
              <h2 id="contact-heading">{t('common.contact') || 'Contact Information'}</h2>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">
                    {t('patients.phone')}
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    {...register('phone')}
                    className={errors.phone ? 'error' : ''}
                    aria-invalid={!!errors.phone}
                    aria-describedby={errors.phone ? 'phone-error' : undefined}
                    onBlur={() => handlePhoneBlur('phone')}
                    autoComplete="tel"
                    placeholder="085 123 4567"
                  />
                  {errors.phone && (
                    <span id="phone-error" className="error-message" role="alert">
                      {errors.phone.message}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="alternatePhone">{t('patients.alternatePhone')}</label>
                  <input
                    id="alternatePhone"
                    type="tel"
                    {...register('alternatePhone')}
                    onBlur={() => handlePhoneBlur('alternatePhone')}
                    autoComplete="tel"
                    placeholder="085 123 4567"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">{t('patients.email')}</label>
                  <input
                    id="email"
                    type="email"
                    {...register('email')}
                    className={errors.email ? 'error' : ''}
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                    autoComplete="email"
                  />
                  {errors.email && (
                    <span id="email-error" className="error-message" role="alert">
                      {errors.email.message}
                    </span>
                  )}
                  <span className="field-hint">
                    {t('patients.ihiContactHint') ||
                      'For IHI matching, mobile phone and/or email should be recorded where possible.'}
                  </span>
                </div>
              </div>
            </div>

            <div className="form-section" role="group" aria-labelledby="address-heading">
              <h2 id="address-heading">{t('common.address')}</h2>
              <div className="form-group">
                <label htmlFor="street">
                  {addressLabels.street}{' '}
                  <span className="required" aria-label={t('accessibility.required')}>
                    *
                  </span>
                </label>
                <input
                  id="street"
                  type="text"
                  {...register('street')}
                  className={errors.street ? 'error' : ''}
                  aria-required="true"
                  aria-invalid={!!errors.street}
                  aria-describedby={errors.street ? 'street-error' : undefined}
                  autoComplete="street-address"
                />
                {errors.street && (
                  <span id="street-error" className="error-message" role="alert">
                    {errors.street.message}
                  </span>
                )}
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">
                    {addressLabels.city}{' '}
                    <span className="required" aria-label={t('accessibility.required')}>
                      *
                    </span>
                  </label>
                  <input
                    id="city"
                    type="text"
                    {...register('city')}
                    className={errors.city ? 'error' : ''}
                    aria-required="true"
                    aria-invalid={!!errors.city}
                    aria-describedby={errors.city ? 'city-error' : undefined}
                    autoComplete="address-level2"
                  />
                  {errors.city && (
                    <span id="city-error" className="error-message" role="alert">
                      {errors.city.message}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="state">{addressLabels.state}</label>
                  <input
                    id="state"
                    type="text"
                    {...register('state')}
                    autoComplete="address-level1"
                    placeholder="e.g., Dublin, Cork"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="postalCode">{addressLabels.postalCode}</label>
                  <input
                    id="postalCode"
                    type="text"
                    {...register('postalCode')}
                    className={errors.postalCode ? 'error' : ''}
                    aria-invalid={!!errors.postalCode}
                    aria-describedby={errors.postalCode ? 'postalCode-error' : 'postalCode-hint'}
                    onBlur={handlePostalCodeBlur}
                    autoComplete="postal-code"
                    placeholder="A12 BCDE"
                  />
                  {errors.postalCode && (
                    <span id="postalCode-error" className="error-message" role="alert">
                      {errors.postalCode.message}
                    </span>
                  )}
                  <span id="postalCode-hint" className="field-hint">
                    {addressLabels.postalCode === 'Eircode' ? 'Format: A12 BCDE' : 'Enter postal code'}
                  </span>
                </div>
                <div className="form-group">
                  <label htmlFor="country">{addressLabels.country}</label>
                  <input
                    id="country"
                    type="text"
                    {...register('country')}
                    defaultValue={defaultCountry}
                    autoComplete="country-name"
                  />
                </div>
              </div>
            </div>
          </>
        )
      case 'clinical-and-lifestyle':
        return (
          <>
            <div className="form-section" role="group" aria-labelledby="medical-heading">
              <h2 id="medical-heading">{t('patients.medicalInfo')}</h2>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="bloodType">{t('patients.bloodType')}</label>
                  <select id="bloodType" {...register('bloodType')}>
                    <option value="">{t('common.select') || 'Select...'}</option>
                    <option value="A+">{t('bloodType.A+')}</option>
                    <option value="A-">{t('bloodType.A-')}</option>
                    <option value="B+">{t('bloodType.B+')}</option>
                    <option value="B-">{t('bloodType.B-')}</option>
                    <option value="AB+">{t('bloodType.AB+')}</option>
                    <option value="AB-">{t('bloodType.AB-')}</option>
                    <option value="O+">{t('bloodType.O+')}</option>
                    <option value="O-">{t('bloodType.O-')}</option>
                    <option value="unknown">{t('bloodType.unknown')}</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="allergies">{t('patients.allergies')}</label>
                <textarea
                  id="allergies"
                  {...register('allergies')}
                  rows={2}
                  placeholder={t('patients.allergiesPlaceholder')}
                  aria-describedby="allergies-hint"
                />
                <span id="allergies-hint" className="field-hint">
                  {t('patients.allergiesPlaceholder')}
                </span>
              </div>
              <div className="form-group">
                <label htmlFor="currentMedications">{t('patients.currentMedications')}</label>
                <textarea
                  id="currentMedications"
                  {...register('currentMedications')}
                  rows={2}
                  placeholder={t('patients.currentMedicationsPlaceholder')}
                  aria-describedby="medications-hint"
                />
                <span id="medications-hint" className="field-hint">
                  {t('patients.currentMedicationsPlaceholder')}
                </span>
              </div>
              <div className="form-group">
                <label htmlFor="pastMedications">Past Medications</label>
                <textarea
                  id="pastMedications"
                  {...register('pastMedications')}
                  rows={2}
                  placeholder="List past medications, comma separated (e.g., Amoxicillin, Prednisone)"
                  aria-describedby="past-medications-hint"
                />
                <span id="past-medications-hint" className="field-hint">
                  Separate multiple medications with commas.
                </span>
              </div>
              <div className="form-group">
                <label htmlFor="chronicConditions">{t('patients.chronicConditions')}</label>
                <textarea
                  id="chronicConditions"
                  {...register('chronicConditions')}
                  rows={2}
                  placeholder={t('patients.chronicConditionsPlaceholder')}
                  aria-describedby="conditions-hint"
                />
                <span id="conditions-hint" className="field-hint">
                  {t('patients.chronicConditionsPlaceholder')}
                </span>
              </div>
              <div className="form-group">
                <label htmlFor="medicalHistory">{t('patients.medicalHistory')}</label>
                <textarea
                  id="medicalHistory"
                  {...register('medicalHistory')}
                  rows={4}
                  aria-describedby="history-hint"
                />
                <span id="history-hint" className="field-hint">
                  Enter detailed medical history
                </span>
              </div>
            </div>

            <div className="form-section" role="group" aria-labelledby="lifestyle-heading">
              <h2 id="lifestyle-heading">Lifestyle Factors</h2>
              <div className="form-group">
                <label htmlFor="smokingStatus">Smoking Status</label>
                <select id="smokingStatus" {...register('smokingStatus')}>
                  <option value="">{t('common.select') || 'Select...'}</option>
                  <option value="never">Never Smoker</option>
                  <option value="ex-smoker">Ex-Smoker</option>
                  <option value="current-daily">Current Smoker (Daily)</option>
                  <option value="current-occasional">Current Smoker (Occasionally/Socially)</option>
                  <option value="e-cigarette">E-cigarette User</option>
                </select>
                <span className="field-hint">
                  Record smoking status at every visit, including type, amount, and history
                </span>
              </div>

              {watch('smokingStatus') && watch('smokingStatus') !== 'never' && (
                <>
                  <div className="form-group">
                    <label htmlFor="smokingDescription">Smoking Details</label>
                    <textarea
                      id="smokingDescription"
                      {...register('smokingDescription')}
                      rows={3}
                      placeholder="e.g., Patient quit 2 years ago; smoked 10 cigarettes daily for 15 years prior. OR Patient smokes daily, approximately 1 pack (20 cigarettes) per day for 10 years."
                      aria-describedby="smoking-description-hint"
                    />
                    <span id="smoking-description-hint" className="field-hint">
                      Provide detailed description of smoking history, amount, and duration
                    </span>
                  </div>
                  {watch('smokingStatus') === 'ex-smoker' && (
                    <div className="form-group">
                      <label htmlFor="smokingCessationDetails">Cessation Details</label>
                      <textarea
                        id="smokingCessationDetails"
                        {...register('smokingCessationDetails')}
                        rows={2}
                        placeholder="e.g., Expressed willingness to quit. Discussed combination of medication and specialist support options. OR Patient not interested in quitting at this time."
                        aria-describedby="cessation-hint"
                      />
                      <span id="cessation-hint" className="field-hint">
                        Note patient's willingness to quit and any cessation support discussed
                      </span>
                    </div>
                  )}
                  {(watch('smokingStatus') === 'current-daily' ||
                    watch('smokingStatus') === 'current-occasional') && (
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="smokingAmountPerDay">Cigarettes per Day</label>
                        <input
                          id="smokingAmountPerDay"
                          type="number"
                          {...register('smokingAmountPerDay', { valueAsNumber: true })}
                          min="0"
                          placeholder="e.g., 20"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="smokingYearsSmoking">Years Smoking</label>
                        <input
                          id="smokingYearsSmoking"
                          type="number"
                          {...register('smokingYearsSmoking', { valueAsNumber: true })}
                          min="0"
                          placeholder="e.g., 15"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="form-group">
                <label htmlFor="alcoholStatus">Alcohol Consumption Status</label>
                <select id="alcoholStatus" {...register('alcoholStatus')}>
                  <option value="">{t('common.select') || 'Select...'}</option>
                  <option value="non-drinker">Non-Drinker</option>
                  <option value="ex-drinker">Ex-Drinker</option>
                  <option value="current-drinker">Current Drinker</option>
                </select>
                <span className="field-hint">
                  Document current consumption levels and identify potential hazardous drinking
                </span>
              </div>

              {watch('alcoholStatus') && watch('alcoholStatus') !== 'non-drinker' && (
                <>
                  <div className="form-group">
                    <label htmlFor="alcoholDescription">Alcohol Consumption Details</label>
                    <textarea
                      id="alcoholDescription"
                      {...register('alcoholDescription')}
                      rows={3}
                      placeholder="e.g., Patient stopped drinking 6 months ago; previously drank beer heavily. OR Patient drinks alcohol 3-4 times per week, typically consumes 2 drinks per occasion (e.g., 1 pint of beer and a spirit). Prefers wine and spirits."
                      aria-describedby="alcohol-description-hint"
                    />
                    <span id="alcohol-description-hint" className="field-hint">
                      Provide detailed description of alcohol consumption patterns
                    </span>
                  </div>
                  {watch('alcoholStatus') === 'current-drinker' && (
                    <>
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="alcoholFrequency">Frequency</label>
                          <input
                            id="alcoholFrequency"
                            type="text"
                            {...register('alcoholFrequency')}
                            placeholder="e.g., 3-4 times per week"
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="alcoholQuantityPerOccasion">Quantity per Occasion</label>
                          <input
                            id="alcoholQuantityPerOccasion"
                            type="text"
                            {...register('alcoholQuantityPerOccasion')}
                            placeholder="e.g., 2 drinks per occasion"
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="alcoholType">Type of Alcohol</label>
                          <input
                            id="alcoholType"
                            type="text"
                            {...register('alcoholType')}
                            placeholder="e.g., Wine and spirits"
                          />
                        </div>
                        <div className="form-group checkbox-group">
                          <input
                            id="alcoholBingeDrinking"
                            type="checkbox"
                            {...register('alcoholBingeDrinking')}
                          />
                          <label htmlFor="alcoholBingeDrinking">
                            Binge Drinking (5+ drinks for men, 4+ for women in one sitting)
                          </label>
                        </div>
                      </div>
                      {watch('alcoholBingeDrinking') && (
                        <div className="form-group">
                          <label htmlFor="alcoholBingeFrequency">Binge Drinking Frequency</label>
                          <input
                            id="alcoholBingeFrequency"
                            type="text"
                            {...register('alcoholBingeFrequency')}
                            placeholder="e.g., once a month at social gatherings"
                          />
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            <div className="form-section">
              <div className="form-group">
                <label htmlFor="notes">{t('patients.notes')}</label>
                <textarea id="notes" {...register('notes')} rows={3} />
              </div>
            </div>
          </>
        )
      case 'coverage':
        return (
          <div className="form-section" role="group" aria-labelledby="coverage-heading">
            <h2 id="coverage-heading">{t('patients.coverageAndSchemes') || 'Health Coverage & Schemes'}</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="patientType">{t('patients.patientType') || 'Patient Type'}</label>
                <select id="patientType" {...register('patientType')}>
                  <option value="">{t('common.select') || 'Select...'}</option>
                  <option value="private">{t('patients.patientTypePrivate') || 'Private'}</option>
                  <option value="gms">{t('patients.patientTypeGms') || 'GMS'}</option>
                  <option value="doctorVisitCard">
                    {t('patients.patientTypeDoctorVisitCard') || 'Doctor Visit Card'}
                  </option>
                  <option value="other">{t('patients.patientTypeOther') || 'Other'}</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="gmsReviewDate">GMS Review Date</label>
                <input id="gmsReviewDate" type="date" {...register('gmsReviewDate')} />
              </div>
              <div className="form-group">
                <label htmlFor="gmsDoctorNumber">GMS Doctor Number</label>
                <input id="gmsDoctorNumber" type="text" {...register('gmsDoctorNumber')} />
              </div>
              <div className="form-group">
                <label htmlFor="gmsDistanceCode">GMS Distance Code</label>
                <input id="gmsDistanceCode" type="text" {...register('gmsDistanceCode')} />
              </div>
            </div>
            {(watch('gmsEligibilityStartDate') || watch('gmsEligibilityEndDate')) && (
              <div className="form-row">
                <div className="form-group">
                  <span className="form-hint">
                    Eligibility period (from checker): {watch('gmsEligibilityStartDate') || '—'} to {watch('gmsEligibilityEndDate') || '—'}
                  </span>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="gmsDispensingStatus">GMS Dispensing Status</label>
                <input id="gmsDispensingStatus" type="text" {...register('gmsDispensingStatus')} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="doctorVisitCardNumber">Doctor Visit Card Number</label>
                <input id="doctorVisitCardNumber" type="text" {...register('doctorVisitCardNumber')} />
              </div>
              <div className="form-group">
                <label htmlFor="doctorVisitCardReviewDate">DVC Review Date</label>
                <input id="doctorVisitCardReviewDate" type="date" {...register('doctorVisitCardReviewDate')} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="doctorVisitCardDoctorNumber">DVC Doctor Number</label>
                <input id="doctorVisitCardDoctorNumber" type="text" {...register('doctorVisitCardDoctorNumber')} />
              </div>
              <div className="form-group">
                <label htmlFor="doctorVisitCardDistanceCode">DVC Distance Code</label>
                <input id="doctorVisitCardDistanceCode" type="text" {...register('doctorVisitCardDistanceCode')} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="dpsNumber">DPS Number</label>
                <input id="dpsNumber" type="text" {...register('dpsNumber')} />
              </div>
              <div className="form-group">
                <label htmlFor="dpsExpiryDate">DPS Expiry Date</label>
                <input id="dpsExpiryDate" type="date" {...register('dpsExpiryDate')} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="insuranceProvider">{t('patients.insuranceProvider')}</label>
                <input id="insuranceProvider" type="text" {...register('insuranceProvider')} />
              </div>
              <div className="form-group">
                <label htmlFor="insurancePolicyNumber">{t('patients.insurancePolicyNumber')}</label>
                <input id="insurancePolicyNumber" type="text" {...register('insurancePolicyNumber')} />
              </div>
              <div className="form-group">
                <label htmlFor="insuranceGroupNumber">{t('patients.insuranceGroupNumber')}</label>
                <input id="insuranceGroupNumber" type="text" {...register('insuranceGroupNumber')} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="insuranceCoverType">Cover Type</label>
                <input id="insuranceCoverType" type="text" {...register('insuranceCoverType')} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group checkbox-group">
                <input id="companySchemeAnPost" type="checkbox" {...register('companySchemeAnPost')} />
                <label htmlFor="companySchemeAnPost">An Post Scheme</label>
              </div>
              <div className="form-group checkbox-group">
                <input id="companySchemeGarda" type="checkbox" {...register('companySchemeGarda')} />
                <label htmlFor="companySchemeGarda">Garda Scheme</label>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="companySchemeOther">Other Company Scheme</label>
              <input id="companySchemeOther" type="text" {...register('companySchemeOther')} />
            </div>
          </div>
        )
      case 'identifiers':
        return (
          <div className="form-section" role="group" aria-labelledby="identifiers-heading">
            <h2 id="identifiers-heading">{t('patients.identifiers') || 'Identifiers'}</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="ppsNumber">PPS Number</label>
                <input id="ppsNumber" type="text" {...register('ppsNumber')} autoComplete="off" />
              </div>
              <div className="form-group">
                <label htmlFor="ihiNumber">IHI Number</label>
                <input id="ihiNumber" type="text" {...register('ihiNumber')} autoComplete="off" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="hospitalIdentifierSystem">Hospital Identifier System</label>
                <input id="hospitalIdentifierSystem" type="text" {...register('hospitalIdentifierSystem')} />
              </div>
              <div className="form-group">
                <label htmlFor="hospitalIdentifierValue">Hospital Identifier</label>
                <input id="hospitalIdentifierValue" type="text" {...register('hospitalIdentifierValue')} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="labIdentifierSystem">Laboratory Identifier System</label>
                <input id="labIdentifierSystem" type="text" {...register('labIdentifierSystem')} />
              </div>
              <div className="form-group">
                <label htmlFor="labIdentifierValue">Laboratory Identifier</label>
                <input id="labIdentifierValue" type="text" {...register('labIdentifierValue')} />
              </div>
            </div>
            <span className="field-hint">
              Record any hospital or laboratory identifier numbers used to identify this patient in external systems.
            </span>
          </div>
        )
      case 'socio-cultural':
        return (
          <div className="form-section" role="group" aria-labelledby="socio-cultural-heading">
            <h2 id="socio-cultural-heading">
              {t('patients.socioCulturalAndCommunication') || 'Socio-cultural & Communication'}
            </h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="occupation">{t('patients.occupation') || 'Occupation'}</label>
                <input id="occupation" type="text" {...register('occupation')} />
              </div>
              <div className="form-group">
                <label htmlFor="religion">{t('patients.religion') || 'Religion'}</label>
                <input id="religion" type="text" {...register('religion')} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="ethnicity">{t('patients.ethnicity') || 'Ethnicity'}</label>
                <select id="ethnicity" {...register('ethnicity')}>
                  <option value="">{t('common.select') || 'Select...'}</option>
                  <option value="whiteIrish">White Irish</option>
                  <option value="irishTraveller">Irish Traveller</option>
                  <option value="otherWhite">Other White</option>
                  <option value="blackIrishOrAfrican">Black Irish or African</option>
                  <option value="otherBlack">Other Black</option>
                  <option value="chinese">Chinese</option>
                  <option value="otherAsian">Other Asian</option>
                  <option value="other">Other</option>
                  <option value="notStated">Not stated</option>
                </select>
              </div>
            </div>

            <div className="form-section">
              <h3>{t('patients.communicationSupport') || 'Communication & Support'}</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="preferredLanguage">
                    {t('patients.preferredLanguage') || 'Preferred language'}
                  </label>
                  <input id="preferredLanguage" type="text" {...register('preferredLanguage')} />
                </div>
                <div className="form-group checkbox-group">
                  <input
                    id="interpreterRequired"
                    type="checkbox"
                    {...register('interpreterRequired')}
                  />
                  <label htmlFor="interpreterRequired">
                    {t('patients.interpreterRequired') || 'Interpreter required'}
                  </label>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="transportNeeds">
                  {t('patients.transportNeeds') || 'Transport needs'}
                </label>
                <textarea id="transportNeeds" {...register('transportNeeds')} rows={2} />
              </div>
              <div className="form-group">
                <label htmlFor="advocacyNeeds">
                  {t('patients.advocacyNeeds') || 'Advocacy needs'}
                </label>
                <textarea id="advocacyNeeds" {...register('advocacyNeeds')} rows={2} />
              </div>
            </div>
          </div>
        )
      case 'practice-registration':
        return (
          <div className="form-section" role="group" aria-labelledby="practice-registration-heading">
            <h2 id="practice-registration-heading">
              {t('patients.practiceRegistration') || 'Practice Registration'}
            </h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="usualGpName">
                  {t('patients.usualGp') || 'Usual GP'}
                </label>
                <input id="usualGpName" type="text" {...register('usualGpName')} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="branchName">
                  {t('patients.branchName') || 'Branch surgery normally attended'}
                </label>
                <input id="branchName" type="text" {...register('branchName')} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="registrationDate">
                  {t('patients.registrationDate') || 'Date of registration with practice'}
                </label>
                <input id="registrationDate" type="date" {...register('registrationDate')} />
              </div>
              <div className="form-group">
                <label htmlFor="deregistrationDate">
                  {t('patients.deregistrationDate') || 'Date of deregistration'}
                </label>
                <input id="deregistrationDate" type="date" {...register('deregistrationDate')} />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="deregistrationReason">
                {t('patients.deregistrationReason') || 'Reason for deregistration'}
              </label>
              <input id="deregistrationReason" type="text" {...register('deregistrationReason')} />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="primaryCareTeamEnrolmentStatus">
                  {t('patients.primaryCareTeamEnrolmentStatus') ||
                    'Enrolment status with a primary care team'}
                </label>
                <input
                  id="primaryCareTeamEnrolmentStatus"
                  type="text"
                  {...register('primaryCareTeamEnrolmentStatus')}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="primaryCareTeamEnrolmentDate">
                  {t('patients.primaryCareTeamEnrolmentDate') ||
                    'Date of enrolment with a primary care team'}
                </label>
                <input
                  id="primaryCareTeamEnrolmentDate"
                  type="date"
                  {...register('primaryCareTeamEnrolmentDate')}
                />
              </div>
              <div className="form-group">
                <label htmlFor="primaryCareTeamDeEnrolmentDate">
                  {t('patients.primaryCareTeamDeEnrolmentDate') ||
                    'Date of de-enrolment with a primary care team'}
                </label>
                <input
                  id="primaryCareTeamDeEnrolmentDate"
                  type="date"
                  {...register('primaryCareTeamDeEnrolmentDate')}
                />
              </div>
            </div>
          </div>
        )
      case 'end-of-life':
        return (
          <div className="form-section" role="group" aria-labelledby="end-of-life-heading">
            <h2 id="end-of-life-heading">{t('patients.endOfLife') || 'End of Life'}</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="dateOfDeath">
                  {t('patients.dateOfDeath') || 'Date of death'}
                </label>
                <input id="dateOfDeath" type="date" {...register('dateOfDeath')} />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="causesOfDeath">
                {t('patients.causesOfDeath') || 'Cause(s) of death'}
              </label>
              <textarea id="causesOfDeath" {...register('causesOfDeath')} rows={3} />
            </div>
          </div>
        )
      case 'next-of-kin':
        return (
          <div className="form-section" role="group" aria-labelledby="next-of-kin-heading">
            <h2 id="next-of-kin-heading">{t('patients.nextOfKin') || 'Next of Kin'}</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nokTitle">
                  {t('patients.salutation')}{' '}
                  <span className="optional" aria-label={t('accessibility.optional')}>
                    ({t('common.optional')})
                  </span>
                </label>
                <select
                  id="nokTitle"
                  {...register('nokTitle')}
                >
                  <option value="">{t('common.none')}</option>
                  {PATIENT_SALUTATIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="nokFirstName">
                  {t('patients.firstName') || 'First name'}
                </label>
                <input id="nokFirstName" type="text" {...register('nokFirstName')} />
              </div>
              <div className="form-group">
                <label htmlFor="nokFamilyName">
                  {t('patients.lastName') || 'Family name'}
                </label>
                <input id="nokFamilyName" type="text" {...register('nokFamilyName')} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nokGender">
                  {t('patients.gender') || 'Gender'}
                </label>
                <select id="nokGender" {...register('nokGender')}>
                  <option value="">{t('common.select') || 'Select...'}</option>
                  <option value="male">{t('gender.male')}</option>
                  <option value="female">{t('gender.female')}</option>
                  <option value="other">{t('gender.other')}</option>
                  <option value="unknown">{t('gender.unknown')}</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="nokRelationshipToPatient">
                  {t('patients.relationshipToPatient') || 'Relationship to patient'}
                </label>
                <input
                  id="nokRelationshipToPatient"
                  type="text"
                  {...register('nokRelationshipToPatient')}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nokHomePhone">
                  {t('patients.homePhone') || 'Home phone'}
                </label>
                <input id="nokHomePhone" type="tel" {...register('nokHomePhone')} />
              </div>
              <div className="form-group">
                <label htmlFor="nokMobilePhone">
                  {t('patients.mobilePhone') || 'Mobile phone'}
                </label>
                <input id="nokMobilePhone" type="tel" {...register('nokMobilePhone')} />
              </div>
              <div className="form-group">
                <label htmlFor="nokEmail">
                  {t('patients.email') || 'Email'}
                </label>
                <input id="nokEmail" type="email" {...register('nokEmail')} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nokAddressLine1">
                  {t('patients.addressLine1') || 'Address line 1'}
                </label>
                <input id="nokAddressLine1" type="text" {...register('nokAddressLine1')} />
              </div>
              <div className="form-group">
                <label htmlFor="nokAddressLine2">
                  {t('patients.addressLine2') || 'Address line 2'}
                </label>
                <input id="nokAddressLine2" type="text" {...register('nokAddressLine2')} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nokCityTown">
                  {t('patients.cityTown') || 'City/Town'}
                </label>
                <input id="nokCityTown" type="text" {...register('nokCityTown')} />
              </div>
              <div className="form-group">
                <label htmlFor="nokCounty">
                  {t('patients.county') || 'County'}
                </label>
                <input id="nokCounty" type="text" {...register('nokCounty')} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nokEircode">
                  {t('patients.eircode') || 'Eircode'}
                </label>
                <input id="nokEircode" type="text" {...register('nokEircode')} />
              </div>
              <div className="form-group">
                <label htmlFor="nokCountry">
                  {t('patients.country') || 'Country'}
                </label>
                <input id="nokCountry" type="text" {...register('nokCountry')} />
              </div>
            </div>
          </div>
        )
      case 'carers':
        return (
          <div className="form-section" role="group" aria-labelledby="carers-heading">
            <h2 id="carers-heading">{t('patients.carers') || 'Carers'}</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="carerTitle">
                  {t('patients.salutation')}{' '}
                  <span className="optional" aria-label={t('accessibility.optional')}>
                    ({t('common.optional')})
                  </span>
                </label>
                <select id="carerTitle" {...register('carerTitle')}>
                  <option value="">{t('common.none')}</option>
                  {PATIENT_SALUTATIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="carerFirstName">
                  {t('patients.firstName') || 'First name'}
                </label>
                <input id="carerFirstName" type="text" {...register('carerFirstName')} />
              </div>
              <div className="form-group">
                <label htmlFor="carerFamilyName">
                  {t('patients.lastName') || 'Family name'}
                </label>
                <input id="carerFamilyName" type="text" {...register('carerFamilyName')} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="carerGender">
                  {t('patients.gender') || 'Gender'}
                </label>
                <select id="carerGender" {...register('carerGender')}>
                  <option value="">{t('common.select') || 'Select...'}</option>
                  <option value="male">{t('gender.male')}</option>
                  <option value="female">{t('gender.female')}</option>
                  <option value="other">{t('gender.other')}</option>
                  <option value="unknown">{t('gender.unknown')}</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="carerDateOfBirth">
                  {t('patients.dateOfBirth') || 'Date of birth'}
                </label>
                <input id="carerDateOfBirth" type="date" {...register('carerDateOfBirth')} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="carerRelationshipToPatient">
                  {t('patients.relationshipToPatient') || 'Relationship to patient'}
                </label>
                <input
                  id="carerRelationshipToPatient"
                  type="text"
                  {...register('carerRelationshipToPatient')}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group checkbox-group">
                <input
                  id="carerIsRegisteredPatient"
                  type="checkbox"
                  {...register('carerIsRegisteredPatient')}
                  aria-describedby="carerIsRegisteredPatient-hint"
                />
                <label htmlFor="carerIsRegisteredPatient">
                  {t('patients.carerIsRegisteredPatient') ||
                    'Carer is a registered patient in this practice'}
                </label>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="carerLinkedPatientId">
                  {t('patients.carerLinkedPatientId') || 'Linked patient ID (if registered)'}
                </label>
                <input id="carerLinkedPatientId" type="text" {...register('carerLinkedPatientId')} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="carerHomePhone">
                  {t('patients.homePhone') || 'Home phone'}
                </label>
                <input id="carerHomePhone" type="tel" {...register('carerHomePhone')} />
              </div>
              <div className="form-group">
                <label htmlFor="carerMobilePhone">
                  {t('patients.mobilePhone') || 'Mobile phone'}
                </label>
                <input id="carerMobilePhone" type="tel" {...register('carerMobilePhone')} />
              </div>
              <div className="form-group">
                <label htmlFor="carerEmail">
                  {t('patients.email') || 'Email'}
                </label>
                <input id="carerEmail" type="email" {...register('carerEmail')} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="carerAddressLine1">
                  {t('patients.addressLine1') || 'Address line 1'}
                </label>
                <input id="carerAddressLine1" type="text" {...register('carerAddressLine1')} />
              </div>
              <div className="form-group">
                <label htmlFor="carerAddressLine2">
                  {t('patients.addressLine2') || 'Address line 2'}
                </label>
                <input id="carerAddressLine2" type="text" {...register('carerAddressLine2')} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="carerCityTown">
                  {t('patients.cityTown') || 'City/Town'}
                </label>
                <input id="carerCityTown" type="text" {...register('carerCityTown')} />
              </div>
              <div className="form-group">
                <label htmlFor="carerCounty">
                  {t('patients.county') || 'County'}
                </label>
                <input id="carerCounty" type="text" {...register('carerCounty')} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="carerEircode">
                  {t('patients.eircode') || 'Eircode'}
                </label>
                <input id="carerEircode" type="text" {...register('carerEircode')} />
              </div>
              <div className="form-group">
                <label htmlFor="carerCountry">
                  {t('patients.country') || 'Country'}
                </label>
                <input id="carerCountry" type="text" {...register('carerCountry')} />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="carerConsentRecordedAt">
                {t('patients.carerConsentRecordedAt') ||
                  'Date on which the carer permitted information to be entered'}
              </label>
              <input id="carerConsentRecordedAt" type="date" {...register('carerConsentRecordedAt')} />
            </div>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className="patient-form"
      noValidate
      aria-label={t('patients.registerNew')}
    >
      <div className="form-section" role="group">
        <div className="form-group">
          <label htmlFor="registeredGpName">
            {t('patients.registeredGp') || 'Registered GP'}{' '}
            <span className="required" aria-label={t('accessibility.required')}>
              *
            </span>
          </label>
          <select
            id="registeredGpName"
            value={watch('registeredGpId') ?? ''}
            onChange={(e) => {
              const id = e.target.value
              setValue('registeredGpId', id || undefined)
              const staff = clinicalStaff?.find((s) => s.id === id)
              setValue('registeredGpName', staff ? formatUserName(staff) : id ? '' : '')
            }}
            className={errors.registeredGpName ? 'error' : ''}
            aria-required="true"
            aria-invalid={!!errors.registeredGpName}
            aria-describedby={errors.registeredGpName ? 'registeredGpName-error' : undefined}
          >
            <option value="">{t('common.select') || 'Select...'}</option>
            {clinicalStaff
              ?.filter((staff) => staff.role === 'doctor' && staff.isActive)
              .map((staff) => (
                <option key={staff.id} value={staff.id}>
                  {formatUserName(staff)}
                </option>
              ))}
          </select>
          {errors.registeredGpName && (
            <span id="registeredGpName-error" className="error-message" role="alert">
              {errors.registeredGpName.message}
            </span>
          )}
        </div>
      </div>

      <Accordion sections={sections} renderSection={renderSection} openSectionFromHash={hashSection} />

      <div className="form-actions">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary"
          disabled={isLoading}
          aria-label={t('common.cancel')}
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={isLoading}
          aria-label={t('common.save')}
        >
          {isLoading ? t('common.loading') : t('common.save')} {t('common.patient') || 'Patient'}
        </button>
      </div>
    </form>
  )
}
